import { Box, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import Switch from 'components/switch';
import { InfoIcon } from 'lucide-react';
import React, { Fragment, useMemo, useState } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import { Summary } from 'views/bluviu-nutrition/features/scenarios/types/scenario-result.type';
import getDecimalPlace from '../../../utils/get-decimal-place';
import ScenarioFeedsTooltip from 'views/bluviu-nutrition/components/scenario-feeds-tooltip';

type CompareTableProps = {
  selectedFeedPlans: Summary[];
};

const tableStyle = {
  height: 600,
  overflowY: 'auto',
  scrollbarWidth: 'auto',
  position: 'relative',
};

const tableCell = {
  borderRight: '1px solid #BFC8CB',
  textAlign: 'start',
};

const tableCellData = {
  textAlign: 'left',
};

const PAGE_NAME_ID = 'bluViuNutrition.viewScenarioResultGt2.compareTable';
const TOOLTIP_INFO_ID = 'bluViuNutrition.viewScenarioResultGt2';

const CompareTable: React.FC<CompareTableProps> = ({ selectedFeedPlans }) => {
  const intl = useIntl();
  const theme = useTheme();
  const [selectedSwitch, setSelectedSwitch] = useState<string | null>(null);

  const feedPlans = useMemo(() => selectedFeedPlans.sort((a, b) => a.feedPlan.localeCompare(b.feedPlan)), [selectedFeedPlans]);

  const columns = useMemo(
    () => ({
      finalWeightGrams: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.finalWeightGrams` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.finalWeightGrams` }),
      },
      daysToReach: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.daysToReach` }),
        info: intl.formatMessage({ id: `${PAGE_NAME_ID}.daysToReach` }),
      },
      weightGainGrams: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.weightGainGrams` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.weightGainGrams` }),
      },
      feedIntakeGrams: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feedIntakeGrams` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.feedIntakeGrams` }),
      },
      bFcr: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.bFcr` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.bFcr` }),
      },
      carcassYieldPercent: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.carcassYieldPercent` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.carcassYieldPercent` }),
      },
      filletFatPercent: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.filletFatPercent` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.filletFatPercent` }),
      },
      hogGainGrams: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.hogGainGrams` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.hogGainGrams` }),
      },
      globalWarmingPotentialCo2KgPerKgGrowth: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.globalWarming` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.globalWarmingPotentialCo2KgPerKgGrowth` }),
      },
      averageFeedCost: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.averageFeedCost` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.averageFeedCost` }),
      },
      feedCostOfGrowth: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feedCostOfGrowth` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.feedCostOfGrowth` }),
      },
      feedCostOfHogGrowth: {
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.feedCostOfHogGrowth` }),
        info: intl.formatMessage({ id: `${TOOLTIP_INFO_ID}.tooltip.feedCostOfHogGrowth` }),
      },
    }),
    []
  );

  const renderDataTable = (
    value: number,
    propertyName: keyof Summary,
    compareValue?: number,
    referenceValue?: number,
    isReferenceColumn?: boolean
  ) => {
    const unit = ['finalWeightGrams', 'weightGainGrams', 'feedIntakeGrams', 'hogGainGrams'].includes(propertyName)
      ? 'gram'
      : propertyName === 'globalWarmingPotentialCo2KgPerKgGrowth'
        ? 'kilogram'
        : undefined;

    const decimalPlace = getDecimalPlace(propertyName);

    if (referenceValue !== undefined && compareValue !== undefined) {
      const relativeValue = ((compareValue - referenceValue) / referenceValue) * 100;
      const absoluteValue = compareValue - referenceValue;

      return (
        <Box display={'flex'} justifyContent={selectedSwitch ? 'space-between' : 'flex-start'}>
          {isReferenceColumn ? (
            <Fragment>
              <FormattedNumber value={value} maximumFractionDigits={decimalPlace} unit={unit} style={unit ? 'unit' : undefined} />
              &nbsp;
              <Typography padding={0} color={'#809298'}>
                ({relativeValue})
              </Typography>
            </Fragment>
          ) : (
            <Fragment>
              {relativeValue > 0 ? '+' : ''}
              <FormattedNumber
                value={absoluteValue}
                maximumFractionDigits={decimalPlace}
                unit={unit}
                style={unit ? 'unit' : undefined}
              />
              &nbsp;
              <Typography padding={0} color={'#809298'}>
                ({<FormattedNumber value={relativeValue} maximumFractionDigits={1} />}%)
              </Typography>
            </Fragment>
          )}
        </Box>
      );
    }

    return <FormattedNumber value={value} maximumFractionDigits={decimalPlace} unit={unit} style={unit ? 'unit' : undefined} />;
  };

  const keys = Object.keys(columns) as (keyof typeof columns)[];

  const referenceColumn = selectedSwitch === null ? undefined : feedPlans.find((plan) => plan.feedPlan === selectedSwitch);

  return (
    <Box>
      <TableContainer sx={{ width: '100%' }} />
      <Box sx={tableStyle}>
        <Table>
          <TableBody sx={{ position: 'sticky', background: theme.palette.common.white, top: 0 }}>
            <TableRow sx={{ boxShadow: '0px 14px 9px -6px #8080801f;' }}>
              <TableCell sx={[tableCellData, tableCell, { width: feedPlans.length > 2 ? '15%' : '20%' }]} variant="head"></TableCell>
              {feedPlans.map((item, idx) => (
                <TableCell
                  key={idx}
                  sx={[
                    tableCellData,
                    {
                      borderRight: idx < feedPlans.length - 1 ? '1px solid #BFC8CB' : 'none',
                      width: feedPlans.length > 2 ? '15%' : '20%',
                    },
                  ]}
                >
                  <Box height={75} display="flex" flexDirection="column" justifyContent={'flex-end'} alignItems="flex-start">
                    <Box display="flex" gap={1}>
                      {item.icon}
                    </Box>
                    <Box overflow="hidden" textOverflow="ellipsis" pt={1}>
                      <ScenarioFeedsTooltip feedPlan={item.feedPlan} feedSummary={item.feeds} display="both" />
                    </Box>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={[tableCellData, tableCell, { width: feedPlans.length > 2 ? '15%' : '20%' }]} />
              {feedPlans.map((item, idx) => (
                <TableCell
                  key={`reference_${idx}`}
                  sx={[
                    tableCellData,
                    {
                      borderRight: idx < feedPlans.length - 1 ? '1px solid #BFC8CB' : 'none',
                      width: feedPlans.length > 3 ? '15%' : '20%',
                      backgroundColor: selectedSwitch !== item.feedPlan ? '' : theme.palette.lightBlue.o25,
                      py: 4,
                    },
                  ]}
                >
                  <Switch
                    label={intl.formatMessage({ id: `${PAGE_NAME_ID}.reference` })}
                    dark
                    value={selectedSwitch === item.feedPlan}
                    onChange={() => setSelectedSwitch(selectedSwitch === item.feedPlan ? null : item.feedPlan)}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
          {keys.map((propertyName, index) => (
            <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#FAFAFA' : 'white' }}>
              <TableCell sx={[tableCellData, tableCell, { width: feedPlans.length > 2 ? '15%' : '20%' }]} variant="head">
                <Tooltip
                  title={
                    <>
                      <Typography fontWeight={'bold'} mb={2}>
                        {columns[propertyName].info}
                      </Typography>
                    </>
                  }
                  placement={'right-end'}
                >
                  <Box display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
                    {columns[propertyName].label}
                    <InfoIcon size={16} />
                  </Box>
                </Tooltip>
              </TableCell>
              {feedPlans.map((feedPlan, idx) => (
                <TableCell
                  key={idx}
                  sx={[
                    tableCellData,
                    {
                      borderRight: idx < feedPlans.length - 1 ? '1px solid #BFC8CB' : 'none',
                      width: feedPlans.length > 3 ? '15%' : '20%',
                      backgroundColor: selectedSwitch !== feedPlan.feedPlan ? '' : theme.palette.lightBlue.o25,
                    },
                  ]}
                >
                  {renderDataTable(
                    feedPlan[propertyName],
                    propertyName,
                    selectedSwitch === null ? undefined : feedPlan[propertyName],
                    selectedSwitch === null ? undefined : referenceColumn?.[propertyName],
                    selectedFeedPlans[idx] === referenceColumn
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </Table>
      </Box>
    </Box>
  );
};

export default CompareTable;
